import { myChannelSchema, type ChannelSlot, type MyChannel } from '@growup/streaming-shared';
import { DateTimeInput, TimeInput } from '@growup/ui';
import { useEffect, useState } from 'react';

import { ApiError, getJson, messageOf, send } from './api';
import { formatDate, formatTime, lisbonInputToIso } from './format';
import { Button, Card, ErrorNote, Field, TextInput } from './ui';

const STATUS: Record<ChannelSlot['status'], string> = {
  requested: 'à espera de resposta',
  proposed: 'proposta de outra hora',
  accepted: 'na grelha',
  declined: 'não aceite',
  cancelled: 'cancelado',
};

const when = (s: ChannelSlot) =>
  `${formatDate(s.startsAt)}, ${formatTime(s.startsAt)}–${formatTime(s.endsAt)}`;

/**
 * Canal Grow uP (GS16) no `/painel`: a grelha das próximas duas semanas no
 * canal da associação, pedir um horário e os meus pedidos (aceitar ou recusar
 * a hora que o programador propõe; cancelar).
 */
export function ChannelCard() {
  const [data, setData] = useState<MyChannel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    getJson('/api/me/channel', myChannelSchema)
      .then(setData)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);

  const act = async (method: string, path: string, body?: unknown) => {
    setError(null);
    try {
      setData(await send(method, path, body, myChannelSchema));
    } catch (e) {
      setError(messageOf(e));
    }
  };

  return (
    <Card
      title="Canal Grow uP"
      actions={
        asking ? undefined : (
          <Button tone="primary" size="sm" onClick={() => setAsking(true)}>
            Pedir horário
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <ErrorNote message={error} />
        <p className="text-gu-ink-muted">
          Transmite no canal da associação,{' '}
          <a
            className="gu-link"
            href={`https://www.twitch.tv/${data?.channel ?? 'growupesportstv'}`}
            target="_blank"
            rel="noreferrer"
          >
            twitch.tv/{data?.channel ?? 'growupesportstv'}
          </a>
          . Pede um horário e o programador do canal responde por email.
        </p>
        {asking ? (
          <RequestForm
            onCancel={() => setAsking(false)}
            onSent={(next) => {
              setData(next);
              setAsking(false);
            }}
          />
        ) : null}
        {data && data.mine.length > 0 ? (
          <section className="flex flex-col gap-2" aria-label="Os meus pedidos">
            <h3 className="font-sub text-label font-bold uppercase">Os meus pedidos</h3>
            <ul className="flex flex-col gap-3">
              {data.mine.map((s) => (
                <li key={s.id} className="flex flex-col gap-1">
                  <span>
                    <span className="font-semibold">{s.title}</span> · {when(s)} ·{' '}
                    {STATUS[s.status]}
                  </span>
                  {s.answerNote ? (
                    <span className="text-caption text-gu-ink-muted">{s.answerNote}</span>
                  ) : null}
                  <span className="flex flex-wrap gap-2">
                    {s.status === 'proposed' ? (
                      <>
                        <Button
                          tone="primary"
                          size="sm"
                          onClick={() =>
                            void act('POST', `/api/me/channel/requests/${s.id}/answer`, {
                              accept: true,
                            })
                          }
                        >
                          Aceitar a nova hora
                        </Button>
                        <Button
                          tone="quiet"
                          size="sm"
                          onClick={() =>
                            void act('POST', `/api/me/channel/requests/${s.id}/answer`, {
                              accept: false,
                            })
                          }
                        >
                          Recusar
                        </Button>
                      </>
                    ) : null}
                    {s.status === 'requested' || s.status === 'accepted' ? (
                      <Button
                        tone="quiet"
                        size="sm"
                        aria-label={`Cancelar: ${s.title}`}
                        onClick={() => void act('DELETE', `/api/me/channel/requests/${s.id}`)}
                      >
                        Cancelar
                      </Button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        <section className="flex flex-col gap-2" aria-label="Próximas duas semanas no canal">
          <h3 className="font-sub text-label font-bold uppercase">Próximas duas semanas</h3>
          {data && data.upcoming.length === 0 ? (
            <p className="text-gu-ink-muted">Ainda nada marcado: o canal está à tua espera.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {data?.upcoming.map((s) => (
                <li key={s.id}>
                  <span className="font-semibold tabular-nums">{when(s)}</span> · {s.title}
                  {s.streamer ? ` · ${s.streamer.displayName}` : ''}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Card>
  );
}

function RequestForm({
  onCancel,
  onSent,
}: {
  onCancel: () => void;
  onSent: (data: MyChannel) => void;
}) {
  const [title, setTitle] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endTime, setEndTime] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  const submit = async () => {
    const starts = lisbonInputToIso(startsAt);
    let ends = /^\d{2}:\d{2}$/.test(endTime)
      ? lisbonInputToIso(`${startsAt.slice(0, 10)}T${endTime}`)
      : null;
    // A acabar depois da meia-noite: no dia seguinte.
    if (starts !== null && ends !== null && ends <= starts) {
      ends = new Date(new Date(ends).getTime() + 86_400_000).toISOString();
    }
    if (starts === null || ends === null) {
      setFields({
        ...(starts === null ? { startsAt: 'Escolhe o dia e a hora.' } : {}),
        ...(ends === null ? { endsAt: 'Escolhe a hora do fim.' } : {}),
      });
      return;
    }
    setBusy(true);
    setError(null);
    setFields({});
    try {
      onSent(
        await send(
          'POST',
          '/api/me/channel/requests',
          { title, startsAt: starts, endsAt: ends, note },
          myChannelSchema,
        ),
      );
    } catch (e) {
      setError(messageOf(e));
      if (e instanceof ApiError) setFields(e.fields);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 border-b border-gu-line pb-4">
      <Field label="O que vais fazer" error={fields.title}>
        <TextInput value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <div className="flex flex-wrap gap-3">
        <Field label="Começa" error={fields.startsAt}>
          <DateTimeInput value={startsAt} onChange={setStartsAt} />
        </Field>
        <Field label="Acaba às" error={fields.endsAt}>
          <TimeInput value={endTime} onChange={setEndTime} />
        </Field>
      </div>
      <Field label="Nota para o programador" optional>
        <TextInput value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <ErrorNote message={error} />
      <div className="flex gap-2">
        <Button tone="primary" disabled={busy || title.trim() === ''} onClick={() => void submit()}>
          Enviar pedido
        </Button>
        <Button tone="quiet" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
